import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import {
	unwrapDataResponse,
	type EnterpriseCompanyBasicUpdateDto,
	type EnterpriseCompanyDescriptionsUpdateDto,
} from '@wport/core';
import { CliError, ExitCode, InvalidArgumentError, ServerClientHttpError } from '../../../lib/errors';
import { enterpriseGet, enterprisePatch } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { readJsonObject, type ReadJsonInputOptions } from '../../../lib/io-helpers';
import { printJson, printWarn } from '../../../lib/output';
import type { WriteCtx } from '../jobs/write-shared';
import {
	assertValidSubmittedItems,
	enforceDeletionConfirmation,
	previewDeletions,
	summarizeItemsDiff,
	getEnterpriseErrorCode,
	type DeletableCandidate,
	type ExistingBlockItem,
} from './block-replace-shared';
import { BASIC_FIELDS, DESCRIPTION_FIELDS, FORBIDDEN_FIELDS, type EnterpriseCompany } from './types';

/** `PATCH /company/basic` 的 6 必填欄位（對齊 `EnterpriseCompanyBasicUpdateDto`，CO-A3）。 */
const REQUIRED_BASIC_FIELDS: ReadonlyArray<string> = [
	'name',
	'industry_category_code',
	'phone_code',
	'phone_number',
	'area_code',
	'address',
];

/**
 * Card A `GET /company`（`EnterpriseCompanyVm`）承諾一定會回傳的 update-ready 欄位。
 * 用來防禦 Card A／Card B 之間的 contract drift（見 Task 2 brief 第 5 點）：用 `in`
 * 而非 falsy 檢查，因為 `null`/`0`/`false` 都是合法值，只有「完全沒有這個 key」才代表契約壞了。
 */
const CONTRACT_REQUIRED_GET_FIELDS: ReadonlyArray<string> = [
	'industry_category_code',
	'area_code',
	'employee_count_range_code',
	'capital_amount',
	'capital_show_status',
	'latest_news',
	'uniform_number',
];

export interface CompanyUpdatePayloads {
	/**
	 * 待送 `PATCH /company/basic` 的整段合併結果；使用者輸入完全沒碰到任何 basic 欄位時為 `null`。
	 * fresh-eyes F-1：型別改用 codegen `EnterpriseCompanyBasicUpdateDto`（`BASIC_FIELDS` 與該 DTO
	 * 的 properties 集合完全對齊，見 `types.ts` BASIC_FIELDS 註解）取代原本的 `Record<string, unknown>`。
	 */
	basic: EnterpriseCompanyBasicUpdateDto | null;
	/**
	 * 待送 `PATCH /company/descriptions` 的部分欄位；使用者輸入完全沒碰到任何 description 欄位時為 `null`。
	 * fresh-eyes F-1：型別改用 codegen `EnterpriseCompanyDescriptionsUpdateDto`。
	 */
	descriptions: EnterpriseCompanyDescriptionsUpdateDto | null;
	/**
	 * 輸入含 `video_links` 時，來自同一次 preflight GET 的 `block_revisions.video_links`（`If-Match`
	 * 用）與現況 `video_links` 陣列（刪除預覽用）；輸入不含 `video_links` 時皆為 `undefined`
	 * （呼叫端據此判斷本次 PATCH /company/descriptions 是否該帶 `If-Match`，見 spec §3.2.2）。
	 */
	videoLinksRevision?: string;
	currentVideoLinks?: ExistingBlockItem[];
}

/**
 * 用已解析好的 JSON 物件，做本地驗證後讀目前公司資料合併成可送出的 PATCH payload
 * （pm_41 Card B Task 2；Task 3 review 後改為接收已解析物件，見下方 Note）。
 * 只組資料、不發 PATCH——`runCompanyUpdate` 負責用回傳值呼叫
 * `PATCH /company/basic`／`PATCH /company/descriptions` 並處理 idempotency key。
 *
 * Note（Task 3 reviewer finding 修正）：本函式不再自己呼叫 `readJsonObject` 讀
 * `--file`/stdin——讀取已上移到呼叫端 `runCompanyUpdate`，讓它能在這裡的 GET 呼叫
 * 之前，先用解析好的 input 判斷 needsBasic/needsDescriptions 並完成 idempotency-key
 * 驗證。`readJsonObject(source, options)` 全流程只能呼叫一次（stdin 只能讀一次），
 * 因此本函式的參數改成 `input: Record<string, unknown>`，呼叫端只讀一次、傳進來。
 *
 * 驗證與早退順序（見各步驟註解說明原因）：
 * 1. 本地擋 FORBIDDEN_FIELDS（唯讀／v1.x 尚未支援欄位）
 * 2. 本地擋不在 BASIC_FIELDS ∪ DESCRIPTION_FIELDS 的未知欄位
 * 3. 「輸入完全沒有可寫欄位」快速失敗——刻意排在 GET 呼叫之前，因為這個判斷不需要
 *    GET 的回應內容，沒必要為了一個注定要失敗的請求多打一次網路
 * 4. GET 現況、防禦性檢查 Card A 契約欄位是否齊全
 * 5. 合併 basic payload；必填欄位缺值（使用者沒給、目前公司資料也沒有）本地擋在 PATCH 之前
 * 6. 組 description payload（僅取使用者輸入中出現的欄位，不與現況合併——descriptions
 *    端點是各自獨立選填，partial 是常態，不同於 basic 的 full-section update）
 */
export async function buildCompanyUpdatePayloads(
	input: Record<string, unknown>,
	ctx: { baseUrl: string; locale: string; timeoutMs: number },
	apiKey: string
): Promise<CompanyUpdatePayloads> {
	const forbiddenFound = Object.keys(input).filter((k) => FORBIDDEN_FIELDS.includes(k));
	if (forbiddenFound.length > 0) {
		throw new InvalidArgumentError(`Field(s) not writable via CLI: ${forbiddenFound.join(', ')}`);
	}

	const writableFields = new Set<string>([...BASIC_FIELDS, ...DESCRIPTION_FIELDS]);
	const unknownFound = Object.keys(input).filter((k) => !writableFields.has(k));
	if (unknownFound.length > 0) {
		throw new InvalidArgumentError(`Unknown field(s): ${unknownFound.join(', ')}`);
	}

	const inputHasBasicField = BASIC_FIELDS.some((f) => f in input);
	const inputHasDescriptionField = DESCRIPTION_FIELDS.some((f) => f in input);
	if (!inputHasBasicField && !inputHasDescriptionField) {
		// 不需要 GET 的回應內容就能判斷，刻意排在 GET 呼叫之前，省一次不必要的網路請求。
		throw new InvalidArgumentError('No writable company fields provided');
	}

	// descriptions-only 的輸入不需要現況合併，但仍需要 GET 一次來滿足下面 basic 的合併需求；
	// basic 的 full-section 合併規則（CO-A3）要求即使只改一個 basic 欄位，也要整段送出，
	// 因此不論本次是否觸及 basic 欄位，都固定發這一次 GET，行為單純、不需要條件式跳過。
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/company'
	);
	const current = unwrapDataResponse<EnterpriseCompany>(body);

	const missingContractFields = CONTRACT_REQUIRED_GET_FIELDS.filter((f) => !(f in current));
	if (missingContractFields.length > 0) {
		// 這是防禦性檢查，理論上不該發生（Card A VM 承諾一定回這些欄位）；一旦發生代表
		// Card A／Card B 契約漂移，不是使用者輸入錯誤，用 ServerOrNetworkError（exit 4）
		// 與其他「後端回應不如預期」的情況（見 unwrapDataResponse）保持一致，
		// 和上面 InvalidArgumentError（exit 2，使用者輸入錯）區隔開來。
		throw new CliError(
			`Backend contract appears broken: GET /company response is missing field(s): ${missingContractFields.join(', ')}`,
			ExitCode.ServerOrNetworkError
		);
	}

	let basic: EnterpriseCompanyBasicUpdateDto | null = null;
	if (inputHasBasicField) {
		// 動態逐欄位組（BASIC_FIELDS 是 ReadonlyArray<string>），先用 Record<string, unknown> 建構，
		// 最後整段 cast 成 codegen DTO——欄位集合已對齊（見 CompanyUpdatePayloads.basic 註解），
		// 這裡的 cast 不是繞過驗證，missingRequired 檢查已涵蓋 DTO 的 required 欄位。
		const merged: Record<string, unknown> = {};
		for (const field of BASIC_FIELDS) {
			merged[field] = field in input ? input[field] : current[field];
		}
		const missingRequired = REQUIRED_BASIC_FIELDS.filter((f) => {
			const v = merged[f];
			return v === null || v === undefined || v === '';
		});
		if (missingRequired.length > 0) {
			const [first] = missingRequired;
			throw new InvalidArgumentError(
				`Missing required field '${first}' — not provided in input and not present on your current company profile` +
					(missingRequired.length > 1 ? ` (also missing: ${missingRequired.slice(1).join(', ')})` : '')
			);
		}
		basic = merged as EnterpriseCompanyBasicUpdateDto;
	}

	let descriptions: EnterpriseCompanyDescriptionsUpdateDto | null = null;
	if (inputHasDescriptionField) {
		const partial: Record<string, unknown> = {};
		for (const field of DESCRIPTION_FIELDS) {
			if (field in input) partial[field] = input[field];
		}
		descriptions = partial as EnterpriseCompanyDescriptionsUpdateDto;
	}

	// `video_links` 不能只靠上面的機械帶過（spec §3.2.2）：後端只在 body 帶 `video_links` 時才要求
	// `If-Match`，且刪除需走 block-replace-shared 的確認流程。這裡從同一次 preflight GET（`current`，
	// 上面已經拿到）取出 revision 與現況陣列，交給 runCompanyUpdate 決定 If-Match／刪除預覽，
	// 不另外多打一次 GET。
	let videoLinksRevision: string | undefined;
	let currentVideoLinks: ExistingBlockItem[] | undefined;
	if ('video_links' in input) {
		currentVideoLinks = (current.video_links as ExistingBlockItem[] | undefined) ?? [];
		videoLinksRevision = (current.block_revisions as Record<string, string> | undefined)?.video_links;
		if (!videoLinksRevision) {
			// 防禦性檢查：同 CONTRACT_REQUIRED_GET_FIELDS 慣例——Card A 承諾 block_revisions 一定帶
			// video_links key，一旦缺席代表後端契約漂移，不是使用者輸入錯誤。
			throw new CliError(
				'Backend contract appears broken: GET /company response is missing block_revisions.video_links',
				ExitCode.ServerOrNetworkError
			);
		}
	}

	return { basic, descriptions, videoLinksRevision, currentVideoLinks };
}

/** Card A `PATCH /company/descriptions` 422 partial-failure body（見本檔 module doc 與 Task 3 brief）。 */
interface DescriptionsPartialFailureBody {
	path?: string;
	updated_sections?: string[];
	failed_section?: string;
}

function isDescriptionsPartialFailureBody(body: unknown): body is DescriptionsPartialFailureBody {
	return (
		!!body &&
		typeof body === 'object' &&
		Array.isArray((body as Record<string, unknown>).updated_sections) &&
		typeof (body as Record<string, unknown>).failed_section === 'string'
	);
}

/** 印出最終公司狀態（成功路徑共用）：JSON 印整份物件，預設文字只印公司名稱一行。 */
function printCompanyResult(company: EnterpriseCompany, format: 'table' | 'json'): void {
	if (format === 'json') {
		printJson(company);
		return;
	}
	process.stdout.write(`Updated company: ${company.name ?? ''}\n`);
}

export interface UpdateIdempotencyFlags {
	idempotencyKey?: string;
	basicIdempotencyKey?: string;
	descriptionsIdempotencyKey?: string;
}

/**
 * 決定 basic / descriptions 兩段各自要用的 idempotency key。
 *
 * 規則（Task 3 brief）：
 * - 只有一段要送：`--idempotency-key` 可用；沒給就各自 fresh UUID。
 * - 兩段都要送：`--idempotency-key` 不可用（同一把 key 打兩個不同 payload 有誤用重放風險），
 *   必須改用 `--basic-idempotency-key` / `--descriptions-idempotency-key`（或都不給、各自 fresh UUID）。
 *   這個檢查必須在任何網路呼叫之前完成（本地 exit 2）。
 */
export function resolveUpdateIdempotencyKeys(
	needsBasic: boolean,
	needsDescriptions: boolean,
	flags: UpdateIdempotencyFlags
): { basicKey?: string; descriptionsKey?: string } {
	const bothNeeded = needsBasic && needsDescriptions;
	if (bothNeeded && flags.idempotencyKey !== undefined) {
		throw new InvalidArgumentError(
			'Both basic and descriptions sections changed: use --basic-idempotency-key and ' +
				'--descriptions-idempotency-key instead of --idempotency-key, to avoid reusing the same key for two different payloads'
		);
	}
	return {
		basicKey: needsBasic ? (flags.basicIdempotencyKey ?? flags.idempotencyKey ?? randomUUID()) : undefined,
		descriptionsKey: needsDescriptions
			? (flags.descriptionsIdempotencyKey ?? flags.idempotencyKey ?? randomUUID())
			: undefined,
	};
}

/**
 * `company update` 核心（pm_41 Card B Task 3）：讀一次 `--file`/stdin JSON、判斷本次
 * 觸及哪些區段並驗證 idempotency-key 旗標（皆不需網路），再交給 `buildCompanyUpdatePayloads`
 * 組好 payload 後，依實際變更的區段送出 `PATCH /company/basic` 與／或
 * `PATCH /company/descriptions`。
 *
 * 讀取與早退順序（Task 3 reviewer finding 修正——原本 `resolveUpdateIdempotencyKeys` 排在
 * `buildCompanyUpdatePayloads` 之後，導致使用者打錯 idempotency-key 旗標時，仍會先觸發
 * `buildCompanyUpdatePayloads` 內部的 `GET /company` 才被擋下來，不符合「idempotency-key
 * 驗證不該有任何網路副作用」的要求）：
 * 1. `readJsonObject(source, options)` 讀一次 JSON（全流程僅此一次，stdin 只能讀一次）
 * 2. 用解析好的 input 判斷 needsBasic/needsDescriptions（`BASIC_FIELDS`/`DESCRIPTION_FIELDS`
 *    是否命中，不需要 GET 回應）
 * 3. `resolveUpdateIdempotencyKeys` 本地驗證 idempotency-key 旗標組合 —— 與下一步
 *    `buildCompanyUpdatePayloads` 內部的 forbidden/unknown-field 檢查何者先做不影響正確性
 *    （兩者都在任何網路呼叫之前），這裡選擇先做 idempotency-key 驗證單純是因為它不需要
 *    再次檢查欄位合法性，可以直接用第 2 步算出的 needsBasic/needsDescriptions
 * 4. `buildCompanyUpdatePayloads(input, ctx, apiKey)`：本地擋 forbidden/unknown 欄位 →
 *    GET 現況 → 合併 → 組 payload（見該函式 docstring）
 *
 * 呼叫順序固定 basic 先、descriptions 後（對齊 Task 2 內部欄位分組順序，也是任意但合理的預設；
 * 兩段彼此欄位不重疊、順序本身不影響最終狀態，唯一有影響的是「第二段失敗時，第一段是否已 commit」，
 * 見下方 partial-failure 分支）。
 *
 * 最終公司狀態的來源：兩段都送時，優先採用 descriptions PATCH 回應內的 `company`
 * （Card A `updateDescriptions` 內部在自己寫入後重跑同一套 view-building 邏輯，
 * 這份 `company` 已經是「basic + descriptions 都寫入後」的最新狀態），不再多打一次 GET。
 */
export async function runCompanyUpdate(
	ctx: WriteCtx & { color?: boolean },
	apiKey: string,
	source: string,
	idempotencyFlags: UpdateIdempotencyFlags,
	options: ReadJsonInputOptions = {},
	confirm = false
): Promise<void> {
	const input = readJsonObject(source, options);
	const needsBasic = BASIC_FIELDS.some((f) => f in input);
	const needsDescriptions = DESCRIPTION_FIELDS.some((f) => f in input);
	const needsVideoLinks = 'video_links' in input;
	if (needsVideoLinks) {
		// fresh-eyes F-2：`video_links` 走的是同一套 block-replace-shared previewDeletions，
		// 需要同款「陣列＋元素皆為 non-null 物件」的本地驗證，且必須在 buildCompanyUpdatePayloads
		// 的 GET 之前就擋下——這個判斷不需要現況資料，沒必要為了注定失敗的輸入多打一次網路。
		assertValidSubmittedItems(input.video_links, 'video_links');
	}
	const { basicKey, descriptionsKey } = resolveUpdateIdempotencyKeys(needsBasic, needsDescriptions, idempotencyFlags);

	const payloads = await buildCompanyUpdatePayloads(input, ctx, apiKey);

	if (needsVideoLinks) {
		// spec §3.2.2：任何一筆既有影片被移除（含非空漏一筆與 `video_links: []`）都要走
		// block-replace-shared 同一套刪除預覽 + confirm 規則，不另寫一份判斷。
		const submitted = (payloads.descriptions?.video_links as DeletableCandidate[] | undefined) ?? [];
		const deletions = previewDeletions(payloads.currentVideoLinks ?? [], submitted, (item) => String(item.url ?? ''));
		enforceDeletionConfirmation('video_links', deletions, confirm, ctx.color ?? false);
	}

	const requestOpts = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey };

	let basicResultCompany: EnterpriseCompany | undefined;
	if (needsBasic) {
		const { body } = await enterprisePatch(requestOpts, '/company/basic', payloads.basic as Record<string, unknown>, {
			idempotencyKey: basicKey,
		});
		basicResultCompany = unwrapDataResponse<EnterpriseCompany>(body);
	}

	if (!needsDescriptions) {
		// basic-only（或兩段都沒有——buildCompanyUpdatePayloads 已擋掉這個情況，不會到這裡）。
		printCompanyResult(basicResultCompany as EnterpriseCompany, ctx.format);
		return;
	}

	try {
		const { body } = await enterprisePatch(
			requestOpts,
			'/company/descriptions',
			payloads.descriptions as Record<string, unknown>,
			{
				idempotencyKey: descriptionsKey,
				// 只有輸入帶 video_links 時才附 If-Match——後端只在這種情況要求它（spec §3.2.2）；
				// 沒帶 video_links 時絕不能傳這個 key（用條件式 spread，不是傳 `undefined`）。
				...(needsVideoLinks ? { ifMatch: payloads.videoLinksRevision } : {}),
			}
		);
		const result = unwrapDataResponse<{ updated_sections: string[]; company: EnterpriseCompany }>(body);
		printCompanyResult(result.company, ctx.format);
	} catch (err) {
		if (needsVideoLinks && err instanceof ServerClientHttpError && err.status === 409) {
			const errorCode = getEnterpriseErrorCode(err);
			if (errorCode === 'idempotency_conflict') {
				throw new InvalidArgumentError(
					'This Idempotency-Key was already used for different request content; change the key (or omit --idempotency-key to let the CLI generate one).'
				);
			}
			if (errorCode !== 'profile_revision_conflict') {
				throw new InvalidArgumentError(
					'Request rejected with an unclassified 409 conflict while updating "video_links"; the CLI will not auto-retry.'
				);
			}
			// spec §3.2.2：video_links 的 409（revision conflict）絕不自動重送（同 replace.ts 對四區塊
			// 409 的既有慣例）。重新 GET 一次算出 video_links 差異、連同（若有）partial-failure body 的
			// updated_sections/failed_section 一起印出，本地 exit 2，交還使用者重新確認。
			const { body: freshBody } = await enterpriseGet(requestOpts, '/company');
			const freshCurrent = unwrapDataResponse<EnterpriseCompany>(freshBody);
			const freshVideoLinks = (freshCurrent.video_links as ExistingBlockItem[] | undefined) ?? [];
			const diff = summarizeItemsDiff(payloads.currentVideoLinks ?? [], freshVideoLinks);
			const partialBody = isDescriptionsPartialFailureBody(err.body) ? err.body : undefined;

			printWarn(`Server state for "video_links" changed since your read: ${diff}.`, ctx.color ?? false);

			const descriptionsError = {
				updated_sections: partialBody?.updated_sections ?? [],
				failed_section: partialBody?.failed_section ?? 'video_links',
				video_links_diff: diff,
			};
			if (ctx.format === 'json') {
				printJson(
					needsBasic
						? { basic_updated: true, basic_company: basicResultCompany, descriptions_error: descriptionsError }
						: { descriptions_error: descriptionsError }
				);
			} else {
				if (needsBasic) process.stdout.write('Basic company info was updated successfully.\n');
				process.stdout.write(
					`Updated sections before failure: ${descriptionsError.updated_sections.join(', ')}; failed section: ${descriptionsError.failed_section}\n`
				);
			}

			throw new InvalidArgumentError(
				`Refusing to auto-retry "video_links" update after a stale revision conflict (409). ${diff}. ` +
					'Re-run `wport enterprise company view` and retry `company update --confirm` against the current state.'
			);
		}

		if (!needsBasic) {
			// descriptions-only 送出失敗：沒有「basic 已成功」需要保留，正常拋出讓頂層印 err.message。
			throw err;
		}
		// basic 已成功、descriptions 這次呼叫失敗：兩種 sub-case（brief 要求都要保留輸出）：
		// (a) descriptions 呼叫整段失敗（網路錯誤、或非 partial 的 4xx，body 沒有 updated_sections/failed_section）
		// (b) descriptions 端點自己內部 partial-failure（422，body 帶 updated_sections/failed_section）
		const descriptionsBody = err instanceof ServerClientHttpError ? err.body : undefined;
		const partial = isDescriptionsPartialFailureBody(descriptionsBody) ? descriptionsBody : undefined;

		if (ctx.format === 'json') {
			printJson({
				basic_updated: true,
				basic_company: basicResultCompany,
				descriptions_error: partial
					? { updated_sections: partial.updated_sections, failed_section: partial.failed_section }
					: { message: err instanceof Error ? err.message : String(err) },
			});
		} else {
			process.stdout.write('Basic company info was updated successfully.\n');
			if (partial) {
				process.stdout.write(
					`Updated sections before failure: ${(partial.updated_sections ?? []).join(', ')}; failed section: ${partial.failed_section}\n`
				);
			} else {
				process.stdout.write(
					`Descriptions update failed entirely: ${err instanceof Error ? err.message : String(err)}\n`
				);
			}
		}
		throw err;
	}
}

interface UpdateFlags {
	file?: string;
	idempotencyKey?: string;
	basicIdempotencyKey?: string;
	descriptionsIdempotencyKey?: string;
	confirm?: boolean;
}

/**
 * `company update` 註冊（pm_41 Card B Task 3；2026-09-04 spec 批 E 擴節：`video_links` 需要
 * `--confirm` 才能刪除既有影片，同 `company replace` 的既有慣例）。
 */
export function registerEnterpriseCompanyUpdate(parent: Command): void {
	parent
		.command('update')
		.description(
			'Update your company basic info and/or descriptions from a JSON file ' +
				'(uniform_number is read-only and cannot be changed via this command). ' +
				'is_representative_visible / is_establishment_date_visible / is_directors_visible ' +
				'default to 0 (hidden) when not set. ' +
				'video_links (in descriptions) uses optimistic locking (If-Match, handled automatically ' +
				'from your latest `company view`); removing any existing video requires --confirm.'
		)
		.requiredOption('--file <path>', 'path to a partial JSON company body, or "-" for stdin')
		.option('--idempotency-key <key>', 'single-section update only: reuse across retries (default: a fresh UUID)')
		.option('--basic-idempotency-key <key>', 'two-section update: idempotency key for PATCH /company/basic')
		.option(
			'--descriptions-idempotency-key <key>',
			'two-section update: idempotency key for PATCH /company/descriptions'
		)
		.option(
			'--confirm',
			'confirm deletion of existing video_links items not present in the input (required when video_links is provided and would remove any existing item)'
		)
		.action(async (flags: UpdateFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runCompanyUpdate(
				ctx,
				key,
				flags.file as string,
				{
					idempotencyKey: flags.idempotencyKey,
					basicIdempotencyKey: flags.basicIdempotencyKey,
					descriptionsIdempotencyKey: flags.descriptionsIdempotencyKey,
				},
				{ timeoutMs: ctx.timeoutMs },
				flags.confirm === true
			);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { REQUIRED_BASIC_FIELDS, CONTRACT_REQUIRED_GET_FIELDS };
